import type { ListingContact, ListingOrigin, ProcessedListingData } from "../../@types/sharedSchema";
// Relative on purpose. This module must stay importable from `functions/`, whose
// tsconfig has no `utils/*` / `components/*` baseUrl alias — the same discipline
// `parser.ts` already keeps. It is also why nothing here touches firebase, the
// DOM, React or i18n: it is a pure function over a listing row.
import { isVirtualPhone } from "../../components/_dashboard/listings/seller/sellerPhone";

/**
 * WHO IS THE SELLER OF THIS LISTING — the single place that question is answered.
 *
 * ## The problem this exists to end
 *
 * `sellerName` / `sellerPhoneNumber` / `agencyName` do not mean the same thing on
 * every row of `yad2-listings-latest`:
 *
 *  - on a SCRAPED row (`yad2`, `madlan`) they are the property's seller, as the
 *    marketplace published them — frequently behind a Yad2 mask (073/055);
 *  - on a `webot` row the mirror Cloud Function `updateWebotListingsOnAlgolia`
 *    OVERWRITES all three with the OWNING REALTOR's own `ownerFullName`,
 *    `ownerPhoneNumber` and `companyName`. There they are the LISTING AGENT.
 *
 * No reader checked `listingOrigin`. So after a realtor claimed a market ad and
 * typed the owner's phone into her copy, the board row, the grid card and the
 * details dialog's מפרסם card all showed HER OWN name and phone back to her as
 * "the seller" — and the save-owner badge cheerfully offered to save her as her
 * own מוכר contact.
 *
 * The owner she actually recorded lives in `listingContacts`, which is what this
 * function reads. The three legacy fields keep their values and meanings, because
 * the seller-name filters, the seller facet, the Postgres `seller_name` column and
 * its indexes, the search audit trail and the MCP `search_listings` path all read
 * them.
 *
 * ## ⚠ THE CALLER HAS TO SUPPLY `listingContacts`. A MIRROR ROW NEVER CARRIES IT.
 *
 * `yad2-listings-latest` is `allow get: if true` — unauthenticated — because the
 * share-link favourites doc hands anonymous buyers the ids of listings a realtor
 * chose to show them. So the recorded owner is deliberately NOT mirrored: doing
 * so would publish his private mobile to those buyers and let them cut our
 * customer out of her own deal. It is read from the authority
 * (`real-estate-listings`, same document id) at display time —
 * `hooks/useRecordedListingContacts` + `withRecordedOwner(hit, recorded)` — which
 * is also the fresher answer, since the mirror lags a save by up to a minute.
 *
 * A caller that hands a bare mirror row straight to this function therefore gets
 * `kind: 'agent'` for a listing that does record an owner. That is the SAFE
 * failure — it withholds a person rather than mislabelling one — but it is still
 * a bug, so every webot surface overlays first.
 *
 * ## The rule
 *
 * Every surface calls THIS function. The `listingOrigin` branch exists in exactly
 * one place, so a new surface cannot reintroduce the bug by reading a field
 * directly, and a caller that renders `kind: 'agent'` as "the seller" is making a
 * visible, reviewable mistake rather than an invisible one.
 */

/**
 * Which PERSON the resolved details describe. Callers must label by this, never
 * by a fixed string:
 *
 *  - `owner`         — the property owner/landlord the realtor recorded herself.
 *                      The only kind that may be called בעל הנכס.
 *  - `scrapedSeller` — the seller as the marketplace published them. May be a
 *                      Yad2 mask, an agency, or a mangled free-text string.
 *  - `agent`         — the realtor who owns this webot listing. A webot row that
 *                      records no owner has NOTHING to say about the seller;
 *                      this is the fallback and it must NOT be labelled one.
 */
export type ListingSellerKind = "owner" | "scrapedSeller" | "agent";

export type ResolvedListingSeller = {
  /** Display name, already trimmed. Absent when the row carries none. */
  name?: string;
  /** Phone as stored — NOT normalized here; contact keying belongs to `sellerPhone.ts`. */
  phone?: string;
  /**
   * Only ever the marketplace/agency behind a scraped seller, or the realtor's own
   * company on an `agent` result. Never populated for `kind: 'owner'`: on a webot
   * row `agencyName` is the LISTING AGENT's company, and attributing it to the
   * owner would invent an employer for a private seller.
   */
  agencyName?: string;
  kind: ListingSellerKind;
  /** True when `phone` looks like a Yad2 mask (073/055) rather than the person's own. */
  isVirtualPhone: boolean;
};

/**
 * The fields a listing row must expose to be resolvable.
 *
 * `Partial` on purpose: mirror rows, Postgres hits and hand-built fixtures all
 * omit different subsets, and a full `ProcessedListingData` is structurally
 * assignable to this, so every caller can pass its hit unchanged.
 */
export type ListingSellerFields = Partial<
  Pick<
    ProcessedListingData,
    "listingOrigin" | "listingContacts" | "sellerName" | "sellerPhoneNumber" | "agencyName"
  >
>;

const clean = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const fullName = (first?: string | null, last?: string | null): string | undefined =>
  clean([clean(first), clean(last)].filter(Boolean).join(" "));

/** A recorded entry counts only once it names someone or carries a number. */
const hasSubstance = (contact?: ListingContact | null): boolean =>
  Boolean(clean(contact?.callerFirstName) || clean(contact?.callerLastName) || clean(contact?.callerPhoneNumber));

/**
 * The recorded owner entry, or undefined.
 *
 * Reads the FIRST entry with any substance rather than strictly `[0]`. These
 * entries come from a `real-estate-listings` document, which legitimately carries
 * an empty row ahead of a filled one — the listing form keeps one around while the
 * realtor is typing, and after she clears one — so `[0]` alone would report "no
 * owner recorded" for a listing that plainly records one.
 */
export function getRecordedListingContact(hit: ListingSellerFields): ListingContact | undefined {
  return (hit?.listingContacts ?? []).find(hasSubstance);
}

/** Does this listing carry an owner the realtor recorded herself? */
export function hasRecordedListingOwner(hit: ListingSellerFields): boolean {
  return Boolean(getRecordedListingContact(hit));
}

/**
 * Resolve who to show as this listing's seller side, and what that person IS.
 *
 * Branches, in order:
 *
 *  1. **webot + a recorded contact** → the OWNER she typed (`kind: 'owner'`).
 *  2. **webot + nothing recorded**   → the realtor (`kind: 'agent'`). Deliberately
 *     still returned, because "who do I call about this listing" is a real
 *     question on a colleague's listing — but the kind says plainly that this is
 *     not a seller, and no caller may render it as one.
 *  3. **anything else** (yad2, madlan, or an origin we were not given) → the
 *     scraped seller. Unknown origin falls here rather than into the webot branch
 *     because mis-labelling a real seller as an agent hides a lead, whereas the
 *     reverse invents one.
 */
export function resolveListingSeller(hit: ListingSellerFields): ResolvedListingSeller {
  const origin: ListingOrigin | undefined = hit?.listingOrigin;

  if (origin === "webot") {
    const recorded = getRecordedListingContact(hit);
    if (recorded) {
      const phone = clean(recorded.callerPhoneNumber);
      return {
        name: fullName(recorded.callerFirstName, recorded.callerLastName),
        phone,
        // No agencyName: on a webot row it is the listing agent's company.
        kind: "owner",
        isVirtualPhone: isVirtualPhone(phone)
      };
    }

    const agentPhone = clean(hit?.sellerPhoneNumber);
    return {
      name: clean(hit?.sellerName),
      phone: agentPhone,
      agencyName: clean(hit?.agencyName),
      kind: "agent",
      isVirtualPhone: isVirtualPhone(agentPhone)
    };
  }

  const phone = clean(hit?.sellerPhoneNumber);
  return {
    name: clean(hit?.sellerName),
    phone,
    agencyName: clean(hit?.agencyName),
    kind: "scrapedSeller",
    isVirtualPhone: isVirtualPhone(phone)
  };
}

/**
 * Overlay the authority's answer about the recorded owner onto a listing row,
 * so a surface can write one line:
 *
 *     resolveListingSeller(withRecordedOwner(hit, recorded))
 *
 * rather than each re-deciding which of the two sources wins.
 *
 * `recorded` comes from `hooks/useRecordedListingContacts` — a read of
 * `real-estate-listings`, which is where the owner lives because the mirror row
 * must not carry him (see the ⚠ block at the top of this file).
 *
 * The rules, and both matter:
 *
 *  - **`undefined` means STILL RESOLVING and leaves the row untouched.** It is
 *    not "no owner". The board's optimistic just-claimed row carries the owner
 *    the realtor typed a second ago, synthesized in memory from the form she
 *    submitted; treating `undefined` as `[]` would wipe him and show her her own
 *    number instead — the precise bug this module exists to end.
 *  - **`[]` WINS.** It is the document she edits, so a stale synthesized owner
 *    must not outlive her deleting him.
 */
export function withRecordedOwner<T extends ListingSellerFields>(
  hit: T,
  recorded: ListingContact[] | undefined
): T {
  if (!recorded) return hit;
  return { ...hit, listingContacts: recorded };
}

export type ResolvedListingAgent = {
  name?: string;
  phone?: string;
  agencyName?: string;
};

/**
 * The REALTOR who owns this listing, for the surfaces that genuinely want her —
 * "מפרסם: <agency>", a call-the-agent action on a colleague's listing.
 *
 * Returns null for any non-webot row: there, the identically-named fields are the
 * property's seller, and handing them back as "the agent" is the same conflation
 * in the other direction.
 */
export function resolveListingAgent(hit: ListingSellerFields): ResolvedListingAgent | null {
  if (hit?.listingOrigin !== "webot") return null;

  const agent: ResolvedListingAgent = {
    name: clean(hit?.sellerName),
    phone: clean(hit?.sellerPhoneNumber),
    agencyName: clean(hit?.agencyName)
  };

  return agent.name || agent.phone || agent.agencyName ? agent : null;
}
